import { subMonths, subWeeks, subDays, format, subYears } from 'date-fns';
import { fieldsAndFriendlyLabelsForFilters } from '@/pages/CandidateInfo/candidateInfoFields';
import {
  developerTypeOptions,
  countriesOptions,
  sourceOptions,
  englishLevelOptions,
  yesOrNoOptions,
  isEmptyOrNotEmptyOptions,
} from '@/utils/dropdownOptions';
import { CandidateFilter } from './CandidateFilter.types';
import { Text } from '../AutoCompleteGenericElements';

const developerTypeOptionsFormatted = developerTypeOptions.map((type) => ({
  value: type,
  node: (props) => <Text {...props}>{type}</Text>,
}));

const countriesOptionsFormatted = countriesOptions.map((option) => ({
  value: option,
  node: (props) => <Text {...props}>{option}</Text>,
}));

const yesOrNoOptionsFormatted = yesOrNoOptions.map((option) => ({
  value: option,
  node: (props) => <Text {...props}>{option}</Text>,
}));

const isEmptyOrNotEmptyOptionsFormatted = isEmptyOrNotEmptyOptions.map((option) => ({
  value: option,
  node: (props) => <Text {...props}>{option}</Text>,
}));

const sourcesOptionsFormatted = sourceOptions.map((option) => ({
  value: option,
  node: (props) => <Text {...props}>{option}</Text>,
}));

const englishLevelOptionsFormatted = englishLevelOptions.map((option) => ({
  value: option.value,
  node: (props) => <Text {...props}>{option.label}</Text>,
}));

const dateOptions = [
  {
    value: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    node: (props) => <Text {...props}>Yesterday</Text>,
  },
  {
    value: format(subWeeks(new Date(), 3), 'yyyy-MM-dd'),
    node: (props) => <Text {...props}>3 weeks go</Text>,
  },
  {
    value: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
    node: (props) => <Text {...props}>3 months ago</Text>,
  },
  {
    value: 'specific-day',
    node: (props) => <Text {...props}>Pick up a date range</Text>,
  },
  {
    value: 'custom-range',
    node: (props) => <Text {...props}>Pick up a date range...</Text>,
  },
];

const formatType = 'yyyy-MM-dd';
const today = format(new Date(), formatType);
export const customRangeOptions = [
  {
    value: [today, format(subDays(new Date(), 7), formatType)],
    node: (props) => <Text {...props}>Last Week</Text>,
  },
  {
    value: [today, format(subMonths(new Date(), 1), formatType)],
    node: (props) => <Text {...props}>Last Month</Text>,
  },
  {
    value: [today, format(subMonths(new Date(), 4), formatType)],
    node: (props) => <Text {...props}>Last Quarter</Text>,
  },
  {
    value: [today, format(subMonths(new Date(), 6), formatType)],
    node: (props) => <Text {...props}>Last Semester</Text>,
  },
  {
    value: [today, format(subYears(new Date(), 1), formatType)],
    node: (props) => <Text {...props}>Last Year</Text>,
  },
];

const allConditions = [
  { value: 'is', label: 'Is' },
  { value: 'isNot', label: 'Is not' },
  { value: 'isLowerThan', label: 'Is Lower Than' },
  { value: 'isHigherThan', label: 'Is Higher Than' },
  { value: 'isLowerOrEqualTo', label: 'Is Lower Or Equal To' },
  { value: 'isHigherOrEqualTo', label: 'Is Higher Or Equal To' },
  { value: 'isEmpty', label: 'Is Empty' },
  { value: 'isNotEmpty', label: 'Is Not Empty' },
  { value: 'arrayContains', label: 'Contains' },
  { value: 'arrayNotContains', label: 'Does Not Contain' },
  { value: 'stringContain', label: 'Contains' },
  { value: 'stringNotContain', label: 'Does Not Contain' },
  { value: 'isBefore', label: 'Is Before' },
  { value: 'isAfter', label: 'Is After' },
  { value: 'isOnOrBefore', label: 'Is On Or Before' },
  { value: 'isOnOrAfter', label: 'Is On Or After' },
  { value: 'isWithin', label: 'Is Within' },
];

const conditionsOptions = allConditions.map((condition) => ({
  value: condition.value,
  node: (props) => <Text {...props}>{condition.label}</Text>,
}));

export const candidateFilters: CandidateFilter[] = [
  {
    property: fieldsAndFriendlyLabelsForFilters.firstName,
    conditions: conditionsOptions.filter((condition) => {
      const nameConditions = ['stringContain', 'stringNotContain'];
      return nameConditions.includes(condition.value);
    }),
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.status,
    conditions: conditionsOptions.filter((condition) => {
      const statusConditions = ['is', 'isNot'];
      return statusConditions.includes(condition.value);
    }),
    groupedOptions: [],
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.developerType,
    conditions: conditionsOptions.filter((condition) => {
      const developerTypeConditions = ['is', 'isNot', 'isEmpty', 'isNotEmpty'];
      return developerTypeConditions.includes(condition.value);
    }),
    values: developerTypeOptionsFormatted,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.ownerId,
    conditions: conditionsOptions.filter((condition) => {
      const techStacksConditions = ['is', 'isNot'];
      return techStacksConditions.includes(condition.value);
    }),
    values: [],
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.skills,
    conditions: conditionsOptions.filter((condition) => {
      const techStacksConditions = ['arrayContains', 'arrayNotContains'];
      return techStacksConditions.includes(condition.value);
    }),
    isMultipleType: true,
    values: [],
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.trioProfileUrl,
    conditions: conditionsOptions.filter((condition) => {
      const profileConditions = ['isEmpty', 'isNotEmpty'];
      return profileConditions.includes(condition.value);
    }),
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.country,
    conditions: conditionsOptions.filter((condition) => {
      const countryConditions = ['is', 'isNot'];
      return countryConditions.includes(condition.value);
    }),
    values: countriesOptionsFormatted,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.source,
    conditions: conditionsOptions.filter((condition) => {
      const sourceConditions = ['is', 'isNot'];
      return sourceConditions.includes(condition.value);
    }),
    values: sourcesOptionsFormatted,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.englishLevel,
    conditions: conditionsOptions.filter((condition) => {
      const englishLevelConditions = ['is', 'isNot'];
      return englishLevelConditions.includes(condition.value);
    }),
    values: englishLevelOptionsFormatted,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.createdAt,
    conditions: conditionsOptions.filter((condition) => {
      const timeConditions = [
        'is',
        'isNot',
        'isBefore',
        'isAfter',
        'isOnOrBefore',
        'isOnOrAfter',
        'isWithin',
      ];
      return timeConditions.includes(condition.value);
    }),
    values: dateOptions,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.updatedAt,
    conditions: conditionsOptions.filter((condition) => {
      const timeConditions = [
        'is',
        'isNot',
        'isBefore',
        'isAfter',
        'isOnOrBefore',
        'isOnOrAfter',
        'isWithin',
      ];
      return timeConditions.includes(condition.value);
    }),
    values: dateOptions,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.isTalentPool,
    conditions: conditionsOptions.filter((condition) => {
      const timeConditions = ['is'];
      return timeConditions.includes(condition.value);
    }),
    values: yesOrNoOptionsFormatted,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.pictureUrl,
    conditions: conditionsOptions.filter((condition) => {
      const pictureUrlConditions = ['is'];
      return pictureUrlConditions.includes(condition.value);
    }),
    values: isEmptyOrNotEmptyOptionsFormatted,
  },
  {
    property: fieldsAndFriendlyLabelsForFilters.hasFintechExperience,
    conditions: conditionsOptions.filter((condition) => {
      const hasFintechExperienceConditions = ['is'];
      return hasFintechExperienceConditions.includes(condition.value);
    }),
    values: yesOrNoOptionsFormatted,
  },
];
